/**
 * Centralized enum ↔ display-label mapping for `public.bay_type`.
 *
 * Single source of truth for:
 *   • UI components rendering a bay's friendly label.
 *   • Any write path that needs to normalize a (possibly user-typed)
 *     label back to the enum value before persisting.
 *
 * The canonical enum values come from migrations:
 *   20251024002525  — initial enum ('2-post', '4-post', 'scissor',
 *                     'adas', 'alignment', 'wash', 'inspection', 'general')
 *   20251031144452  — adds 'alignment-adas'
 *   20251207103544  — adds 'flat'
 *   20260315033901  — adds 'single-post'
 *
 * The label set mirrors `src/components/facility/InspectorPanel.tsx`'s
 * `BAY_TYPE_OPTIONS` so chips, dropdowns, and `technicians.bay_assignments`
 * stay aligned.
 *
 * Why this file exists
 * --------------------
 * Sharing the label list across the codebase prevents future regressions
 * like the one that surfaced as the toast:
 *
 *   Toast: "Bay assignments not fully saved"
 *   Description: invalid input value for enum bay_type: "2-Post Lift"
 *
 * which happened because a write path sent a friendly label into the
 * `bay_type` enum column. The DB write path now lives entirely inside the
 * `sync_tech_bay_assignments` RPC (migration 20260601130000) — the client
 * sends bay UUIDs, never labels — but this module gives any future
 * write-site one canonical place to look up the enum value first.
 */

/**
 * Canonical `bay_type` enum values, in canonical order. Exposed as a
 * `const`-asserted tuple (not just a union type) so consumers like
 * `zod`'s `z.enum(...)` can drive validation directly off the same
 * list — preventing the schema and the picker from drifting apart.
 */
export const BAY_TYPE_ENUM_VALUES = [
  '2-post',
  '4-post',
  'scissor',
  'single-post',
  'alignment',
  'alignment-adas',
  'adas',
  'wash',
  'inspection',
  'flat',
  'general',
] as const;

export type BayTypeEnum = (typeof BAY_TYPE_ENUM_VALUES)[number];

/**
 * Onboarding/setup-time default for non-specialty technician bays
 * (Batch 16-B: 2026-05-30). Pre-bump the wizard's generator wrote
 * `'general'` for every non-specialty slot — a legacy alias that
 * Birdseye / ShopFloorEditor / InspectorPanel all render as "Flat"
 * but that doesn't match the stored value the operator would expect
 * after picking "Flat" from the new per-bay dropdown. New shops
 * created via the lean wizard now write the canonical `'flat'`
 * enum value; legacy shops with stored `'general'` rows continue to
 * render correctly via `bayTypeToLabel('general') === 'Flat'`.
 */
export const DEFAULT_SETUP_BAY_TYPE: BayTypeEnum = 'flat';

const ENUM_TO_LABEL: Record<BayTypeEnum, string> = {
  '2-post':         '2-Post Lift',
  '4-post':         '4-Post Lift',
  'scissor':        'Scissor Lift',
  'single-post':    'Single-Post Lift',
  'alignment':      'Alignment',
  'alignment-adas': 'Alignment + ADAS',
  'adas':           'ADAS',
  'wash':           'Wash',
  'inspection':     'Inspection',
  'flat':           'Flat',
  'general':        'Flat',
};

// Case-insensitive label → enum. Includes a few historical UI variants
// (e.g. "Alignment & ADAS", "Wash Bay") so any code that still carries a
// stale label can still be normalized at the boundary.
const LABEL_TO_ENUM: Record<string, BayTypeEnum> = {
  '2-post lift':       '2-post',
  '4-post lift':       '4-post',
  'scissor lift':      'scissor',
  'single-post lift':  'single-post',
  'alignment':         'alignment',
  'alignment + adas':  'alignment-adas',
  'alignment & adas':  'alignment-adas',
  'adas':              'adas',
  'wash':              'wash',
  'wash bay':          'wash',
  'inspection':        'inspection',
  'flat':              'flat',
  'flat bay':          'flat',
  'general':           'general',
};

const ENUM_VALUES = new Set<string>(Object.keys(ENUM_TO_LABEL));

/**
 * Canonical picker option list (Batch 16-A: 2026-05-30).
 *
 * Hoisted here from `src/components/facility/InspectorPanel.tsx` so every
 * surface that renders a bay-type dropdown — InspectorPanel, Birdseye,
 * ShopFloorEditor, the lean setup wizard's specialty step — pulls its
 * options from the same place. Labels are sourced from `ENUM_TO_LABEL`
 * above so they cannot drift.
 *
 * Order matches the pre-Batch-16 InspectorPanel ordering exactly so the
 * dropdown the operator sees is unchanged in sequence. The 11th entry
 * (`general`) is kept because some legacy `shop_bays` rows still carry
 * the value `'general'` and the InspectorPanel's `<Select value=...>`
 * needs a matching `<SelectItem>` to highlight the trigger correctly.
 * Both `'flat'` and `'general'` render as the canonical "Flat" label;
 * deduplicating the picker — i.e. dropping `'general'` and soft-migrating
 * stored values to `'flat'` — is deferred until a later batch so this one
 * stays zero-behavior-change.
 */
export const BAY_TYPE_PICKER_OPTIONS: ReadonlyArray<{
  value: BayTypeEnum;
  label: string;
}> = [
  { value: '2-post',         label: ENUM_TO_LABEL['2-post'] },
  { value: '4-post',         label: ENUM_TO_LABEL['4-post'] },
  { value: 'scissor',        label: ENUM_TO_LABEL['scissor'] },
  { value: 'single-post',    label: ENUM_TO_LABEL['single-post'] },
  { value: 'alignment',      label: ENUM_TO_LABEL['alignment'] },
  { value: 'adas',           label: ENUM_TO_LABEL['adas'] },
  { value: 'alignment-adas', label: ENUM_TO_LABEL['alignment-adas'] },
  { value: 'wash',           label: ENUM_TO_LABEL['wash'] },
  { value: 'inspection',     label: ENUM_TO_LABEL['inspection'] },
  { value: 'flat',           label: ENUM_TO_LABEL['flat'] },
  { value: 'general',        label: ENUM_TO_LABEL['general'] },
];

/** True when `value` is a canonical `bay_type` enum value. */
export function isBayTypeEnumValue(value: unknown): value is BayTypeEnum {
  return typeof value === 'string' && ENUM_VALUES.has(value);
}

/** Display label for an enum value. Falls back to the raw value. */
export function bayTypeToLabel(value: string): string {
  return ENUM_TO_LABEL[value as BayTypeEnum] ?? value;
}

/**
 * Coerce a possibly-display-label string back into a `bay_type` enum value.
 *
 * - If the input is already an enum value, it's returned unchanged.
 * - If the input matches a known display label (case-insensitive), the
 *   corresponding enum value is returned.
 * - Otherwise returns `null` so callers can decide whether to skip the
 *   value or surface a warning instead of letting it reach Postgres.
 */
export function labelToBayTypeEnum(input: string): BayTypeEnum | null {
  if (typeof input !== 'string' || input.length === 0) return null;
  if (isBayTypeEnumValue(input)) return input;
  return LABEL_TO_ENUM[input.trim().toLowerCase()] ?? null;
}

/** UUID v4-ish shape guard used at the write boundary. */
export function looksLikeUuid(value: unknown): value is string {
  return typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}
